import { Injectable } from '@angular/core';
import { Observable, from, switchMap, catchError, throwError } from 'rxjs';
import { ReceivingLogApiService } from './api/receiving-log-api.service';
import { AssetsDataService } from '../../service/assets-data.service';
import { convertPhotosToFiles } from '../../shared/helpers/photos-utils';
import { AddReceivingLogRequest } from '../../models/logs/AddReceivingLogRequest';
import { AddReceivingLogResponse } from '../../models/logs/AddReceivingLogResponse';
import { LogEntryPhoto } from '../../models/logs/photos/LogEntryPhoto.interface';

export type LogSubmissionFailureReason = 'photoProcessingFailed' | 'requestFailed';

/**
 * Typed error emitted by LogSubmissionService.submit. `userMessage` is ready to
 * show to the user (sourced from the configured toast text or the server message).
 */
export class LogSubmissionError extends Error {
  constructor(
    public reason: LogSubmissionFailureReason,
    public userMessage: string,
    public override cause?: unknown
  ) {
    super(userMessage);
    this.name = 'LogSubmissionError';
  }
}

/**
 * Stateless receiving-log submission: converts the entry's photos to files and
 * posts the log (with email data and photos) to the backend in one observable
 * pipeline. No BehaviorSubject side effects or success callbacks — callers
 * (desktop form, mobile flow) subscribe and own loading/toast/navigation concerns.
 */
@Injectable({
  providedIn: 'root',
})
export class LogSubmissionService {
  constructor(
    private _api: ReceivingLogApiService,
    private _assetsDataService: AssetsDataService
  ) {}

  /**
   * Submits a new receiving log with its photos.
   *
   * @param receivingLogRequest The log row, optional email data, and blob names.
   * @param photos The shipment proof photos to upload alongside the log.
   * @param damagePhotos Per-item damage photos, pre-named `damage-{receiptIndex}-{n}.{ext}`;
   *   uploaded in a separate multipart field so the backend attaches them to receipt rows.
   * @param slipPhotos Packing-slip pages, pre-named `packing-slip-{n}.{ext}`; uploaded in
   *   their own multipart field so the backend labels them as the slip, not shipment photos.
   * @param onUploadProgress Called with bytes-sent while the multipart body uploads.
   *   Lets a caller time out on a *stalled* upload rather than a slow one — on the
   *   warehouse network the body transfer is nearly the whole request duration.
   * @returns The backend response (per-step statuses + isSuccess); errors with a
   *   LogSubmissionError when photo conversion or the request fails.
   */
  public submit(
    receivingLogRequest: AddReceivingLogRequest,
    photos: LogEntryPhoto[],
    damagePhotos: LogEntryPhoto[] = [],
    slipPhotos: LogEntryPhoto[] = [],
    onUploadProgress?: (loaded: number, total?: number) => void
  ): Observable<AddReceivingLogResponse> {
    const toastText = this._assetsDataService.GetUiConfigData().toastText;

    return from(
      Promise.all([
        convertPhotosToFiles(photos),
        convertPhotosToFiles(damagePhotos),
        convertPhotosToFiles(slipPhotos),
      ])
    ).pipe(
      catchError((err) => {
        console.error('Error converting photos to files:', err);
        return throwError(
          () =>
            new LogSubmissionError(
              'photoProcessingFailed',
              toastText.photos.processing.error,
              err
            )
        );
      }),
      switchMap(([photoFiles, damagePhotoFiles, slipPhotoFiles]: [File[], File[], File[]]) =>
        this._api.postReceivingLog(receivingLogRequest, photoFiles, damagePhotoFiles, slipPhotoFiles, onUploadProgress).pipe(
          catchError((err) =>
            throwError(
              () =>
                new LogSubmissionError(
                  'requestFailed',
                  this.requestErrorMessage(err),
                  err
                )
            )
          )
        )
      )
    );
  }

  /**
   * Maps a failed submission request to a user-facing message: payload-too-large
   * gets the photo size message, a server-provided message is passed through,
   * anything else gets the generic submit error.
   */
  private requestErrorMessage(err: any): string {
    const toastText = this._assetsDataService.GetUiConfigData().toastText;
    // Errors arrive wrapped by handleErrorFunction as { message, details, originalError }
    const httpError = err?.originalError ?? err;
    if (httpError?.status === 413) {
      return toastText.photos.processing.sizeError;
    }
    if (httpError?.error?.message) {
      return httpError.error.message;
    }
    return toastText.logEntry.submit.error;
  }
}
